/**
 * Shop Pay express-checkout link sync.
 *
 * `snippets/shop-pay-button.liquid` renders each instance server-side with
 * the correct initial href and visibility, so this module only needs to
 * keep instances current after the cart changes client-side — it never
 * runs an initial-load sync.
 */
(() => {
  'use strict';
  if (window.ShopPayCheckout) return;

  const INSTANCE_SELECTOR = '[data-js="shop-pay-checkout"]';
  const MIN_CART_ITEMS = 2;

  /**
   * Build the Shop Pay checkout entry URL. `payment=shop_pay` sends the
   * buyer straight into Shop Pay from the standard checkout redirect, so
   * the live session cart (with any applied discounts) is used as-is.
   * @returns {string}
   */
  const buildHref = () => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}checkout?payment=shop_pay`;
  };

  /**
   * Apply a fresh href to every instance, and count-based visibility to
   * `data-mode="cart"` instances (`data-mode="manual"` owners manage their
   * own visibility). A null count leaves visibility untouched.
   * @param {number|null} itemCount
   * @returns {void}
   */
  const applyState = (itemCount) => {
    const href = buildHref();
    document.querySelectorAll(INSTANCE_SELECTOR).forEach((el) => {
      el.setAttribute('href', href);
      if (el.dataset.mode === 'cart' && typeof itemCount === 'number') {
        el.hidden = itemCount < MIN_CART_ITEMS;
      }
    });
  };

  /**
   * Sync every instance from a cart payload, fetching the cart when the
   * caller has none.
   * @param {object|null} cartData - cart.js-shaped payload or null
   * @returns {Promise<void>}
   */
  const sync = async (cartData) => {
    if (cartData && typeof cartData.item_count === 'number') {
      applyState(cartData.item_count);
      return;
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`);
      const cart = await response.json();
      applyState(typeof cart.item_count === 'number' ? cart.item_count : null);
    } catch {
      applyState(null);
    }
  };

  document.addEventListener('cart:updated', (event) => {
    sync(event.detail?.cartData ?? null);
  });

  document.addEventListener('click', (event) => {
    const instance = event.target.closest(INSTANCE_SELECTOR);
    if (instance && instance.dataset.disabled === 'true') {
      event.preventDefault();
    }
  });

  window.ShopPayCheckout = Object.freeze({ buildHref, sync });
})();
